import { db } from "../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../config/firebase";

const SETTINGS_COLLECTION = "bookingSettings";
const RULES_COLLECTION = "availabilityRules";
const OVERRIDES_COLLECTION = "availabilityOverrides";

const waitForAuthUser = () =>
  new Promise((resolve) => {
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      () => {
        unsubscribe();
        resolve(null);
      }
    );
  });

const requireAuthenticatedUserId = async () => {
  const user = await waitForAuthUser();
  const userId = user?.uid;
  if (!userId) {
    throw new Error("User not authenticated");
  }
  return userId;
};

const getDefaultSettings = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Istanbul";
  return {
    durationMinutes: 30,
    timezone: tz,
    minLeadValue: 24,
    minLeadUnit: "hour",
    maxFutureValue: 30,
    maxFutureUnit: "day",
    bufferMinutes: 0,
    dailyLimit: 0,
    requiresApproval: false,
    provider: "google",
    providerConnected: false,
  };
};

const getBookingSettings = async (userId) => {
  const resolvedUserId = userId || await requireAuthenticatedUserId();
  const docRef = doc(db, SETTINGS_COLLECTION, resolvedUserId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    return { ...getDefaultSettings(), userId: resolvedUserId };
  }
  const { createdAt, updatedAt, ...rest } = docSnap.data();
  return { ...getDefaultSettings(), ...rest, userId: resolvedUserId };
};

const saveBookingSettings = async (settings) => {
  const userId = await requireAuthenticatedUserId();
  const docRef = doc(db, SETTINGS_COLLECTION, userId);
  const payload = {
    ...settings,
    userId,
    updatedAt: Timestamp.now(),
  };
  await setDoc(docRef, payload, { merge: true });
  const docSnap = await getDoc(docRef);
  return { ...getDefaultSettings(), ...docSnap.data(), userId };
};

const getAvailabilityRules = async (userId) => {
  const resolvedUserId = userId || await requireAuthenticatedUserId();
  const colRef = collection(db, RULES_COLLECTION);
  const q = query(colRef, where("userId", "==", resolvedUserId));
  const snap = await getDocs(q);
  const rules = [];
  snap.forEach((docSnap) => {
    const { createdAt, updatedAt, ...rest } = docSnap.data();
    rules.push({ id: docSnap.id, ...rest });
  });
  return rules;
};

const saveAvailabilityRules = async (rules, userId) => {
  const resolvedUserId = userId || await requireAuthenticatedUserId();
  const colRef = collection(db, RULES_COLLECTION);
  const existingSnap = await getDocs(query(colRef, where("userId", "==", resolvedUserId)));
  const existingIds = new Set();
  existingSnap.forEach((docSnap) => existingIds.add(docSnap.id));

  const enabledRules = rules.filter((rule) => rule.enabled);
  const incomingIds = new Set(enabledRules.filter((rule) => rule.id).map((rule) => rule.id));
  const batch = writeBatch(db);

  existingSnap.forEach((docSnap) => {
    if (!incomingIds.has(docSnap.id)) {
      batch.delete(docSnap.ref);
    }
  });

  enabledRules.forEach((rule) => {
    const payload = {
      userId: resolvedUserId,
      weekday: rule.weekday,
      startTime: rule.startTime,
      endTime: rule.endTime,
      isActive: true,
      updatedAt: Timestamp.now(),
    };
    if (rule.id) {
      batch.set(doc(db, RULES_COLLECTION, rule.id), payload, { merge: true });
    } else {
      batch.set(doc(colRef), { ...payload, createdAt: Timestamp.now() });
    }
  });

  await batch.commit();
  return getAvailabilityRules(resolvedUserId);
};

const getAvailabilityOverrides = async (userId) => {
  const resolvedUserId = userId || await requireAuthenticatedUserId();
  const colRef = collection(db, OVERRIDES_COLLECTION);
  const q = query(colRef, where("userId", "==", resolvedUserId));
  const snap = await getDocs(q);
  const overrides = [];
  snap.forEach((docSnap) => {
    const { createdAt, updatedAt, ...rest } = docSnap.data();
    overrides.push({ id: docSnap.id, ...rest });
  });
  return overrides;
};

const saveAvailabilityOverrides = async (overrides, userId) => {
  const resolvedUserId = userId || await requireAuthenticatedUserId();
  const colRef = collection(db, OVERRIDES_COLLECTION);
  const existingSnap = await getDocs(query(colRef, where("userId", "==", resolvedUserId)));
  const existingIds = new Set();
  existingSnap.forEach((docSnap) => existingIds.add(docSnap.id));

  const incomingIds = new Set(overrides.filter((item) => item.id).map((item) => item.id));
  const batch = writeBatch(db);

  existingSnap.forEach((docSnap) => {
    if (!incomingIds.has(docSnap.id)) {
      batch.delete(docSnap.ref);
    }
  });

  overrides.forEach((override) => {
    if (!override.date) return;
    const payload = {
      userId: resolvedUserId,
      date: override.date,
      startTime: override.startTime || null,
      endTime: override.endTime || null,
      isAvailable: override.isAvailable ?? true,
      updatedAt: Timestamp.now(),
    };
    if (override.id) {
      batch.set(doc(db, OVERRIDES_COLLECTION, override.id), payload, { merge: true });
    } else {
      batch.set(doc(colRef), { ...payload, createdAt: Timestamp.now() });
    }
  });

  await batch.commit();
  return getAvailabilityOverrides(resolvedUserId);
};

const bookingConfigService = {
  getDefaultSettings,
  getBookingSettings,
  saveBookingSettings,
  getAvailabilityRules,
  saveAvailabilityRules,
  getAvailabilityOverrides,
  saveAvailabilityOverrides,
};

export default bookingConfigService;
